/**
 * ComparisonShot — a lightweight, self-contained shot runner.
 *
 * Each instance manages its own BallRenderer, ShotTracer, FlightRecorder,
 * and GroundPhysics. Used by GearTestOrchestrator to fire 10+ shots
 * simultaneously during comparison mode.
 *
 * Does NOT share any state with ShotSimulator except the Rapier world
 * (which is frame-idempotent via markNewFrame/stepWorld).
 */

import type { Scene } from '@babylonjs/core/scene';
import type { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import type { EnvironmentConfig, FlightState, ShotResult, SwingParams, Vec3 } from '../types';
import { SurfaceType } from '../types';
import { DEFAULT_ENVIRONMENT, FLIGHT_SAMPLE_INTERVAL } from '../constants';
import { advanceShotFlight, createFlightSetup, flightEnvironment } from '../physics/FlightSetup';
import { GroundPhysics } from '../physics/GroundPhysics';
import { FlightRecorder } from '../simulation/FlightRecorder';
import { computeShotResult, formatShotResult } from '../simulation/ShotResult';
import { BallRenderer } from '../rendering/BallRenderer';
import { ShotTracer } from '../rendering/ShotTracer';
import { predictFlightFrame } from '../camera/predictFlightFrame';

export type ComparisonColor = { r: number; g: number; b: number; hex: string };

export class ComparisonShot {
  private ball: BallRenderer;
  private tracer: ShotTracer;
  private recorder = new FlightRecorder();
  private ground = new GroundPhysics();

  private flightState: FlightState | null = null;
  private prevFlightState: FlightState | null = null;
  private phase: 'idle' | 'flight' | 'ground' | 'rest' = 'idle';
  private physicsFrameCount = 0;
  private groundTime = 0;
  private context = flightEnvironment(DEFAULT_ENVIRONMENT);
  private environment: EnvironmentConfig;
  private launchSpinRate = 0;

  private _result: ShotResult | null = null;
  private _formatted: Record<string, string> | null = null;
  readonly color: ComparisonColor;
  private framingPoints: Vec3[] = [];

  getFramingPoints(): Vec3[] {
    const rest = this._result?.restPosition;
    return rest ? [...this.framingPoints, rest] : this.framingPoints;
  }

  getRecordedPositions(): Vec3[] {
    return this.recorder.getFrames().map(frame => frame.position);
  }

  constructor(scene: Scene, shadowGen: CascadedShadowGenerator, color: ComparisonColor,
    environment: EnvironmentConfig = DEFAULT_ENVIRONMENT,
  ) {
    this.environment = { ...environment };
    this.color = color;
    this.ball = new BallRenderer(scene, shadowGen, true);
    this.ball.setColor(color.r, color.g, color.b);
    this.tracer = new ShotTracer(scene);
    this.tracer.setColor(color.r, color.g, color.b);
  }

  /** Launch the comparison shot. */
  launch(params: SwingParams): void {
    this.ground.cleanup();
    this.recorder.clear();
    const setup = createFlightSetup(params, this.environment);
    this.framingPoints = predictFlightFrame(params, this.environment);
    this.flightState = setup.state;
    this.context = setup.context;
    this.launchSpinRate = setup.state.spinRate;
    this.prevFlightState = { ...this.flightState };
    this.phase = 'flight';
    this.physicsFrameCount = 0;
    this.groundTime = 0;
    this._result = null;
    this._formatted = null;

    this.recorder.record(this.flightState);
  }

  /** Step physics. Call once per physics tick. */
  physicsUpdate(dt: number): void {
    if (this.phase === 'flight') {
      this.updateFlight(dt);
    } else if (this.phase === 'ground') {
      this.updateGround(dt);
    }
  }

  /** Render update with interpolation. Call once per render frame. */
  renderUpdate(alpha: number): void {
    if (this.phase === 'flight' && this.flightState && this.prevFlightState) {
      this.ball.setInterpolatedPosition(
        this.prevFlightState.position,
        this.flightState.position,
        alpha,
      );
      // Rebuild tracer every frame for smooth growth
      this.tracer.buildFromFrames(this.recorder.getFrames());
    } else if (this.phase === 'ground') {
      const pos = this.ground.getPosition();
      this.ball.setPosition(pos);
      // Continue building tracer during ground phase → rollout visible
      this.tracer.buildFromFrames(this.recorder.getFrames());
    }
  }

  isComplete(): boolean {
    return this.phase === 'rest';
  }

  getResult(): { result: ShotResult; formatted: Record<string, string> } | null {
    if (!this._result || !this._formatted) return null;
    return { result: this._result, formatted: this._formatted };
  }

  /** Access the shot tracer for highlighting. */
  getTracer(): ShotTracer {
    return this.tracer;
  }

  /** Get the launch spin rate (rpm) for this shot. */
  getSpinRate(): number {
    return this.launchSpinRate;
  }

  /** Get current carry distance in meters (for real-time leaderboard). Returns 0 if not in flight. */
  getCurrentCarryMeters(): number {
    const frames = this.recorder.getFrames();
    if (frames.length < 2) return 0;
    const start = frames[0];
    const latest = frames[frames.length - 1];
    const dx = latest.position.x - start.position.x;
    const dz = latest.position.z - start.position.z;
    return Math.sqrt(dx * dx + dz * dz);
  }

  /** Check if shot is still in the air (not on ground yet). */
  isInFlight(): boolean {
    return this.phase === 'flight';
  }

  /** Get current ball world position (for camera tracking). Returns null if idle. */
  getCurrentPosition(): Vec3 | null {
    if (this.phase === 'flight' && this.flightState) {
      return this.flightState.position;
    }
    if (this.phase === 'ground') {
      return this.ground.getPosition();
    }
    if (this.phase === 'rest' && this._result) {
      return this._result.restPosition;
    }
    return null;
  }

  /** Get the flight recorder for replay access. */
  getRecorder(): FlightRecorder {
    return this.recorder;
  }

  /** Clean up all resources (meshes, Rapier body). */
  dispose(): void {
    this.ball.dispose();
    this.tracer.clearAll();
    this.ground.cleanup();
  }

  /** Dispose heavy GPU resources (ball mesh, ground physics) but keep tracer line visible.
   *  Used between spec tranches to free memory on mobile while keeping flight paths drawn. */
  disposeHeavy(): void {
    this.ball.dispose();
    this.ground.cleanup();
  }

  // ── Private: Flight Phase ─────────────────────────────────────────────────

  private updateFlight(dt: number): void {
    if (!this.flightState) return;
    this.prevFlightState = this.flightState;
    const next = advanceShotFlight(this.flightState, dt, this.context);
    this.flightState = next.state;
    this.physicsFrameCount++;

    if (next.landed) {
      const landing = next.state;
      this.recorder.record(landing, 'landing');
      this.tracer.buildFromFrames(this.recorder.getFrames());
      this.phase = 'ground';
      this.groundTime = landing.time;
      this.ground.startGroundPhase(landing.position, landing.velocity, SurfaceType.Fairway,
        landing.spinRate, landing.spinAxis);
    } else if (this.physicsFrameCount % FLIGHT_SAMPLE_INTERVAL === 0) {
      this.recorder.record(this.flightState);
    }
  }

  // ── Private: Ground Phase ─────────────────────────────────────────────────

  private updateGround(dt: number): void {
    this.groundTime += dt;
    const pos = this.ground.step(dt);

    // Record ground frames for tracer rollout visualization
    if (this.physicsFrameCount % (FLIGHT_SAMPLE_INTERVAL * 4) === 0) {
      this.recorder.recordGround(this.groundTime, pos, this.ground.getVelocity());
    }
    this.physicsFrameCount++;

    if (this.ground.atRest) {
      this.recorder.recordGround(this.groundTime, pos, { x: 0, y: 0, z: 0 });
      this.ball.setPosition(pos);
      this.phase = 'rest';

      this._result = computeShotResult(this.recorder.getFrames());
      this._formatted = formatShotResult(this._result);
    }
  }
}
